/* eslint-disable eqeqeq */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { Button, Dropdown, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { useEffect, useState, useRef } from "react";
import ReactPaginate from "react-paginate";
import { useTranslate } from "../hooks/LanguageProvider";
import LineChartComponent from "../components/Charts/Gpsactivity";
import { PropagateLoader } from "react-spinners";
import ExcelJS from "exceljs";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/fr";
import { Link, useNavigate } from "react-router-dom";

import {
  engineStat,
  BarreReseau,
  ValidatePosition,
  getAddressFromCoordinates,
  GetAddressesFromCoords,
  formatDateForAlgeriaTimeZone,
  useClipboard,
  toTimestamp,
  BarreSpeed,
  addHiddenColumn,
  removeHiddenColumn,
  getHiddenColumns,
} from "../utilities/functions";
import MapDrawer from "../components/Map/MapDrawer";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
const PAGE_ID = 4; // ✅ Fleet page id_permission/id_page

export function Fleet() {
  interface Vehicle {
    id: number;
    immatriculation_vehicule: string;
    psn_dispositif: string;
    id_puce: number | null;
    numero_puce: string | null;
    operateur_puce: string | null;
    nom_groupe: string;
    nom_user: string;
    prenom_user: string;
    nom_conducteur: string;
    prenom_conducteur: string;
    TIMESTAMP: string;
    GPSDIST: number;
    GSMLVL: number | null;
    NST: number;
    ENGINESTAT: number;
    LAT: number;
    LON: number;
    vehicule_type: string;
    telephone_conducteur: string;
    SOG: number;
    IB_CODE: string;
    state: number;
  }

  const navigate = useNavigate();
  const { translate } = useTranslate();

  const user_id = Number(localStorage.getItem("userid") || 0);

  const [list_Fleet, setItems] = useState<Vehicle[]>([]);
  let [total, setTotal] = useState(0);
  let [limit, setSelectedLimit] = useState(10);
  const [pageCount, setPageCount] = useState(0);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [address, setAddress] = useState<string>("");
  const [loadingAddress, setLoadingAddress] = useState<boolean>(false);
  const [selectedHeader, setSelectedHeader] = useState("");
  const [loading, setLoading] = useState<boolean>(false);

  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [markerCoordinates, setMarkerCoordinates] = useState({ LAT: 0, LON: 0 });
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const [typeSearch, setTypeSearch] = useState("Immatriculation");
  const [selectedSearch, setSelectedSearch] = useState(translate("Immatriculation"));
  const [type, setType] = useState(0);
  const [search, setSearch] = useState("");
  const [column, setSortColumn] = useState("0");
  const [sort, setSort] = useState("ASC");
  const [page, setPage] = useState<number>(1);
  const [endDate, setEndDate] = useState<Dayjs>(dayjs());

  const { copyToClipboard, copiedId } = useClipboard(translate("Matriculation Copied"));

  /********************** columns **********************/
  const columns = [
    { key: "immatriculation_vehicule", label: translate("Matriculation") },
    { key: "psn_dispositif", label: translate("PSN") },
    { key: "nom_groupe", label: translate("Vehicles Groups") },
    { key: "nom_user", label: translate("User") },
    { key: "nom_conducteur", label: translate("Driver") },
    { key: "TIMESTAMP", label: translate("Date Time") },
    { key: "GPSDIST", label: translate("Distance") },
    { key: "GSMLVL", label: translate("GSM") },
    { key: "SOG", label: translate("Speed") },
  ] as const;

  type ColumnKey = (typeof columns)[number]["key"];

  const defaultSelectedColumns = columns.reduce((acc, c) => {
    (acc as any)[c.key] = true;
    return acc;
  }, {} as Record<ColumnKey, boolean>);

  const [selectedColumns, setSelectedColumns] = useState<Record<ColumnKey, boolean>>(
    defaultSelectedColumns
  );

  // ✅ Load hidden columns from DB on page load
  useEffect(() => {
    (async () => {
      if (!user_id) return;
      try {
        const hidden = await getHiddenColumns(user_id, PAGE_ID); // ["psn_dispositif", ...]
        setSelectedColumns((prev) => {
          const next = { ...prev };
          columns.forEach((c) => {
            next[c.key] = !hidden.includes(c.key);
          });
          return next;
        });
      } catch (e) {
        console.error(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_id]);

  // ✅ Save preference without redirect (stay on Fleet)
  const handleColumnChange = async (columnKey: ColumnKey) => {
    if (!user_id) return;

    const currentlyVisible = selectedColumns[columnKey];
    const nextVisible = !currentlyVisible;

    // update UI immediately
    setSelectedColumns((prev) => ({ ...prev, [columnKey]: nextVisible }));

    try {
      if (!nextVisible) {
        // HIDE => add row in preferences_columns
        await addHiddenColumn({
          id_user: user_id,
          id_page: PAGE_ID,
          column_name: columnKey,
        });
      } else {
        // SHOW => remove row
        await removeHiddenColumn({
          id_user: user_id,
          id_page: PAGE_ID,
          column_name: columnKey,
        });
      }
    } catch (err) {
      console.error(err);

      // rollback UI if API fails
      setSelectedColumns((prev) => ({ ...prev, [columnKey]: currentlyVisible }));
    }
  };


  /********************** filters types **********************/
  type mainFilter = {
    engineContact: boolean;
    lastTransmission: boolean;
    position: boolean;
    gsmNetwork: boolean;
    fullMemory: boolean;
    relayActive: boolean;
    [key: string]: boolean;
  };

  type Filters = {
    engineContact: string | null;
    lastTransmission: string | null;
    position: string | null;
    gsmNetwork: number | null;
    date: string | null;
  };

  const [mainFilters, setMainFilters] = useState<mainFilter>({
    engineContact: false,
    lastTransmission: false,
    position: false,
    gsmNetwork: false,
    fullMemory: false,
    relayActive: false,
  });

  const [filters, setFilters] = useState<Filters>({
    engineContact: null,
    lastTransmission: null,
    position: null,
    gsmNetwork: null,
    date: null,
  });

  const handleCheckboxChangeFilters = (filterName: keyof typeof mainFilters) => {
    setMainFilters((prevFilters) => {
      const updatedFilters = {
        ...prevFilters,
        [filterName]: !prevFilters[filterName],
      };

      if (!updatedFilters[filterName]) {
        setFilters((prevSelections) => ({
          ...prevSelections,
          [filterName]: null,
        }));
      }

      return updatedFilters;
    });
  };


  const handleRadioChange = (filterName: string, value: any) => {
    setFilters((prevSelections) => {
      const updatedFilters = {
        ...prevSelections,
        [filterName]: value,
      };


      getFleet(limit, page, search, type, column, sort, updatedFilters);


      console.log(updatedFilters);

      return updatedFilters;
    });
  };



  const [percentage, setPercentage] = useState(100);

  const handleSliderChange = (event: any) => {
    const newPercentage = event.target.value;
    setPercentage(newPercentage);
  };

  const handleSliderMouseUp = (event: any) => {
    const newPercentage = event.target.value;
    setPercentage(event.target.value);
    setFilters((prevSelections) => {
      const updatedFilters = {
        ...prevSelections,
        gsmNetwork: parseFloat(newPercentage),
      };


      getFleet(limit, page, search, type, column, sort, updatedFilters);


      console.log(updatedFilters);

      return updatedFilters;
    });
  };



  useEffect(() => {
    const refreshInterval = setInterval(() => {
      setLoading(false);
      getFleet(limit, page, search, type, column, sort, filters, false)
    }, 15000);

    return () => clearInterval(refreshInterval); // Nettoyage à la destruction
  }, [limit, page, search, type, column, sort, filters]);


  /********************** API fetch **********************/
  const getFleet = async (
    limit: number,
    page: number,
    search: string,
    type: number,
    column: string,
    sort: string,
    filters: Filters,
    showLoader: boolean = true
  ) => {
    const bodyData = JSON.stringify({
      limit,
      page,
      search,
      type,
      id_user: user_id,
      column,
      sort,
      filters,
    });

    try {
      if (showLoader) setLoading(true);

      const total_pages = await fetch(`${backendUrl}/api/fleet/totalpage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: bodyData,
        mode: "cors",
      });

      const totalPages = await total_pages.json();
      total = totalPages?.[0]?.total ?? 0;
      setTotal(total);

      const vehicleResponse = await fetch(`${backendUrl}/api/fleet/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: bodyData,
        mode: "cors",
      });

      if (!vehicleResponse.ok) {
        console.error("Error retrieving vehicles list");
        return;
      }

      const data = await vehicleResponse.json();
      setPageCount(Math.ceil(total / limit));
      setItems(data);
      return data;
    } catch (error) {
      console.error("Error retrieving data from table", error);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => {
    if (!user_id) return;
    getFleet(limit, page, search, type, column, sort, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_id, limit, search, type]);

  /********************** UI handlers **********************/
  const handleRowClick = async (rowId: string, lat: number, lon: number) => {
    setExpandedRow(expandedRow === rowId ? null : rowId);

    if (expandedRow !== rowId) {
      try {
        setLoadingAddress(true);
        const addr = await getAddressFromCoordinates(lat, lon);
        setAddress(addr);
      } catch (error) {
        console.error("Error fetching address:", error);
      } finally {
        setLoadingAddress(false);
      }
    }
  };

  const handleSelectChange = async (event: any) => {
    const newValue = event.target.value;
    setPage(1);
    setSelectedLimit(newValue);
    const commentsFormServer = await getFleet(parseInt(newValue), 1, search, type, column, sort, filters);
    setItems(commentsFormServer || []);
    window.scrollTo(0, 0);
  };

  const handlePageClick = async (data: any) => {
    let pg = data.selected + 1;
    await getFleet(limit, pg, search, type, column, sort, filters);
    setPage(pg);
    window.scrollTo(0, 0);
  };

  // click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        // no-op
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // excel selection
  const handleCheckboxChange = (index: number, isChecked: boolean) => {
    const newSelectedRows = new Set(selectedRows);
    if (isChecked) newSelectedRows.add(index);
    else newSelectedRows.delete(index);
    setSelectedRows(newSelectedRows);
  };

  const handleSelectAllCheckboxChange = (isChecked: boolean) => {
    setSelectAllChecked(isChecked);
    const newSelectedRows = new Set<number>();
    if (isChecked) list_Fleet.forEach((_, index) => newSelectedRows.add(index));
    setSelectedRows(newSelectedRows);
  };

  async function exportToExcel() {
    const selectedData = list_Fleet.filter((_, index) => selectedRows.has(index));

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Fleet Data");

    const headers = [
      "Date Time",
      "Vehicle",
      "PSN",
      "Vehicles Groups",
      "User",
      "Driver",
      "Distance",
      "GSM",
      "State",
      "ENGINESTAT",
      "Speed",
      "Address",
    ];

    worksheet.addRow(headers);
    worksheet.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "DDDDDD" } };

    const locations = selectedData.map((item) => ({ id: item.id, lat: item.LAT, lon: item.LON }));
    const addresses = await GetAddressesFromCoords(locations);

    for (let i = 0; i < selectedData.length; i++) {
      const item = selectedData[i];
      const addr = addresses[i]?.address ?? "";

      const engineState =
        item.ENGINESTAT === 0 ? "Engine off" :
          item.ENGINESTAT === 1 && item.SOG > 5 ? "Engine running" :
            item.ENGINESTAT === 1 ? "Engine on" : "Unknown";

      worksheet.addRow([
        formatDateForAlgeriaTimeZone(item.TIMESTAMP),
        item.immatriculation_vehicule,
        item.psn_dispositif,
        item.nom_groupe,
        `${item.nom_user} ${item.prenom_user}`,
        `${item.nom_conducteur} ${item.prenom_conducteur}`,
        `${Math.floor(item.GPSDIST / 1000)} km`,
        item.GSMLVL !== null ? Number(item.GSMLVL).toFixed(0) + "%" : "0",
        item.NST === 1 ? "Valid position" : "Invalid position",
        engineState,
        `${item.SOG}`,
        addr,
      ]);
    }

    worksheet.columns.forEach((col) => (col.width = Math.max(col.width ?? 0, 20)));

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const currentDate = new Date();
    const dateString = currentDate.toISOString().split("T")[0];
    const timeString = currentDate.toTimeString().split(" ")[0].replace(/:/g, "-");
    const fileName = `fleet_Repport_${dateString}_${timeString}.xlsx`;

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();

    handleSelectAllCheckboxChange(false);
  }

  const handleMapIconClick = (LAT: number, LON: number, vehicle: Vehicle) => {
    setMarkerCoordinates({ LAT, LON });
    setSelectedVehicle(vehicle);
    setShowMapModal(true);
  };

  const menuItems = [
    translate("Matriculation"),
    translate("PSN"),
    translate("Vehicles Groups"),
    translate("User"),
    translate("Driver"),
    translate("GPSDIST"),
    translate("GSMLVL"),
    translate("SOG"),
  ];

  const handleTypeSearch = (selectedValue: string) => {
    switch (selectedValue) {
      case translate("Matriculation"):
        setSelectedSearch(translate("Matriculation"));
        setType(0);
        break;
      case translate("PSN"):
        setSelectedSearch(translate("PSN"));
        setType(1);
        break;
      case translate("Vehicles Groups"):
        setSelectedSearch(translate("Vehicles Groups"));
        setType(2);
        break;
      case translate("User"):
        setSelectedSearch(translate("User"));
        setType(3);
        break;
      case translate("Driver"):
        setSelectedSearch(translate("Driver"));
        setType(4);
        break;
      default:
        break;
    }
    setTypeSearch(translate(selectedValue));
  };

  const handleAdvancedSearch = async (event: any) => {
    const newValue = event.target.value;
    setSearch(newValue);
    await getFleet(limit, page, newValue, type, column, sort, filters);
  };

  const handleResetSearch = async () => {
    setSearch("");
    await getFleet(limit, page, "", type, column, sort, filters);
  };

  const handleSortingColum = (currentColumn: string) => {
    const nextSort = sort === "DESC" ? "ASC" : "DESC";
    setSort(nextSort);
    setSortColumn(currentColumn);
    setPage(1);
    getFleet(limit, 1, search, type, currentColumn, nextSort, filters);
  };

  // refresh
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      getFleet(limit, page, search, type, column, sort, filters, false);
    }, 15000);

    return () => clearInterval(refreshInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, page, search, type, column, sort, filters]);

  const handleEndDateChange = (newDate: Dayjs | null) => {
    if (!newDate) return;
    setEndDate(newDate);

    setFilters((prev) => {
      const updated = { ...prev, date: toTimestamp(newDate.toISOString()) };
      getFleet(limit, page, search, type, column, sort, updated);
      return updated;
    });
  };

  /********************** RENDER **********************/
  return (
    <>
      <div className="row">
        <div className="col-sm-6 col-md-6 d-flex">
          <h4>
            <i className="fa fa-light fa-chart-line"></i>{" "}
            {translate("Fleet view")} ({total})
          </h4>
        </div>

        <div className="float-right col-sm-6 col-md-6">
          <button
            className="btn btn-outline-info btn-generate-csv float-right"
            onClick={exportToExcel}
            disabled={selectedRows.size === 0}
          >
            <i className="las la-file-alt mr-3"></i>
            {translate("Export")}
          </button>
        </div>
      </div>

      <div className="row">
        <div className="col-md-4" style={{ margin: "0px 0px 10px 0px", padding: "10px" }}>
          <div className="input-group">
            <Dropdown>
              <Dropdown.Toggle variant="link" id="dropdown-custom" className="custom-toggle">
                <i className="fas fa-chevron-down" style={{ fontSize: "20" }}></i>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {menuItems.map((item, index) => (
                  <Dropdown.Item
                    key={index}
                    onClick={() => handleTypeSearch(item)}
                    eventKey={item}
                    active={typeSearch === item}
                    className={typeSearch === item ? "select-active" : ""}
                  >
                    {item}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>

            <input
              type="text"
              placeholder={` ${translate("Search by")} ${translate(typeSearch)}`}
              onChange={handleAdvancedSearch}
              className="form-control"
            />
            <Button variant="secondary" onClick={handleResetSearch} className="btn-reset">
              <i className="las la-times" style={{ color: "#fff" }}></i>
            </Button>
          </div>
        </div>


          <div className="col-md-8 d-flex justify-content-end align-items-center">

          <button
            className="btn position-fixed end-0 z-3"
            style={{
              top: '30%',
              background: "#ff7e41",
              border: '2px solid #ff7e41',
              color: 'white',
              fontSize: '16px',
              transition: 'all 0.3s ease',
            }}
            type="button"
            data-bs-toggle="offcanvas"
            data-bs-target="#drawerMenu"
            aria-controls="drawerMenu"
          >
            <i className="las la-filter" style={{ fontSize: "24px" }}></i>
          </button>  

        <div
          className="offcanvas offcanvas-end"
          tabIndex={-1}
          id="drawerMenu"
          aria-labelledby="drawerMenuLabel"
        >
          <div className="d-flex justify-content-between align-items-start p-4 border-bottom border-1  position-relative"
            style={{
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '6px',
              borderBottom: '1px solid #ddd !important'
            }}
          >
            <div>
              <h6 className="mb-1 fw-bold">{translate('advancedSearch')}</h6>
              <p className="mb-0 text-muted small">{translate('refineSearch')}</p>
            </div>

            {/* Close */}
            <button
              type="button"
              className="btn   d-flex align-items-center"
              data-bs-dismiss="offcanvas"
              style={{ fontSize: "20px" }}
              aria-label="Close"
            >
              <i className="las la-times"></i>
            </button>
          </div>

          <div className="offcanvas-body">
            {/* Engine Contact */}
            <div className="row pt-1">
              <label className="col-6" style={{ cursor: "pointer" }} onChange={() => handleCheckboxChangeFilters("engineContact")}>
                <input
                  type="checkbox"
                  name="engineContact"
                  checked={mainFilters.engineContact}
                  onChange={() => { }}
                  style={{ display: "none" }} // Cacher la case à cocher
                />
                <i className="las la-cogs" style={{ color: "#ff7e41", fontSize: "20px" }}></i>
                <span style={{ fontWeight: mainFilters.engineContact ? "bold" : "normal" }}>
                  {" "}{translate('contactMotor')}
                </span>
              </label>
              {mainFilters.engineContact && (
                <div className="col-6">
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label>
                      <input
                        type="radio"
                        name="engineContact"
                        value="off"
                        onChange={() => handleRadioChange('engineContact', 'off')}
                        checked={filters.engineContact === 'off'}
                      />

                      <span>{" "}{translate('stop')}</span>
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="engineContact"
                        value="on"
                        onChange={() => handleRadioChange('engineContact', 'on')}
                        checked={filters.engineContact === 'on'}
                      />

                      <span>{" "}{translate('running')}</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Last Transmission */}
            <div className="row pt-1">
              <label className="col-6" style={{ cursor: "pointer" }} onChange={() => handleCheckboxChangeFilters("lastTransmission")}>
                <input
                  type="checkbox"
                  name="lastTransmission"
                  checked={mainFilters.lastTransmission}
                  onChange={() => { }}
                  style={{ display: "none" }} // Cacher la case à cocher
                />
                <i className="las la-sync-alt" style={{ color: "#ff7e41", fontSize: "20px" }}></i>
                <span style={{ fontSize: '14px', fontWeight: mainFilters.lastTransmission ? "bold" : "normal" }}>
                  {" "}{translate('lastTransmission')}
                </span>
              </label>
              {mainFilters.lastTransmission && (
                <div className="col-6">
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label>
                      <input
                        type="radio"
                        name="lastTransmission"
                        value="last_1h"
                        onChange={() => handleRadioChange('lastTransmission', 'last_1h')}
                        checked={filters.lastTransmission === 'last_1h'}
                      />
                      <span>{" "}{translate('last1Hour')}</span>
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="lastTransmission"
                        value="last_24h"
                        onChange={() => handleRadioChange('lastTransmission', 'last_24h')}
                        checked={filters.lastTransmission === 'last_24h'}
                      />
                      <span>{" "}{translate('last24Hours')}</span>
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="lastTransmission"
                        value="last_7d"
                        onChange={() => handleRadioChange('lastTransmission', 'last_7d')}
                        checked={filters.lastTransmission === 'last_7d'}
                      />
                      <span>{" "}{translate('last7Days')}</span>
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="lastTransmission"
                        value="present_month"
                        onChange={() => handleRadioChange('lastTransmission', 'present_month')}
                        checked={filters.lastTransmission === 'present_month'}
                      />
                      <span>{" "}{translate('currentMonth')}</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Position */}
            <div className="row pt-1">
              <label className="col-6" style={{ cursor: "pointer" }} onChange={() => handleCheckboxChangeFilters("position")}>
                <input
                  type="checkbox"
                  name="positionStatus"
                  checked={mainFilters.position}
                  onChange={() => { }}
                  style={{ display: "none" }} // 
                //disabled={!mainFilters.lastTransmission} // Disabled si lastTransmission 
                />
                <i className="las la-map-marker-alt" style={{ color: "#ff7e41", fontSize: "20px" }}></i>
                <span style={{ fontWeight: mainFilters.position ? "bold" : "normal" }}>
                  {" "}{translate('position')}
                </span>
              </label>
              {mainFilters.position && (
                <div className="col-6">
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label>
                      <input
                        type="radio"
                        name="positionStatus"
                        value="invalid"
                        onChange={() => handleRadioChange('position', 'invalid')}
                        checked={filters.position === 'invalid'}
                      />
                      <span>{" "}{translate('invalid')}</span>
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="positionStatus"
                        value="valid"
                        onChange={() => handleRadioChange('position', 'valid')}
                        checked={filters.position === 'valid'}
                      />
                      <span>{" "}{translate('valid')}</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* GSM Network */}
            <div className="row pt-1">
              <label className="col-6" style={{ cursor: "pointer" }} onChange={() => handleCheckboxChangeFilters("gsmNetwork")}>
                <input
                  type="checkbox"
                  name="gsmNetwork"
                  checked={mainFilters.gsmNetwork}
                  onChange={() => { }}
                  style={{ display: "none" }} // Cacher la case à cocher
                />
                <i className="las la-signal" style={{ color: "#ff7e41", fontSize: "20px" }}></i>
                <span style={{ fontSize: '14px', fontWeight: mainFilters.gsmNetwork ? "bold" : "normal" }}>
                  {" "}{translate('gsmNetwork')}
                </span>
              </label>
              {mainFilters.gsmNetwork && (
                <div className="col-6">
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="range"
                      id="percentageSlider"
                      min="0"
                      max="100"
                      value={percentage}
                      onChange={handleSliderChange}
                      onMouseUp={handleSliderMouseUp}
                      style={{ margin: '10px 0', width: '80%' }}
                    />
                    <span>{percentage}%</span>
                  </div>
                </div>
              )}
            </div>


            <div>

              <div style={{ borderTop: "1px solid #ddd", padding: "15px 0" }}><h5>{translate("Filter by date: ")}</h5></div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <i
                  className="las la-calendar d-inline icon-report"
                  style={{ marginRight: "0px", fontSize: "1.2rem" }}
                ></i>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
                  <DateTimePicker
                    //  label="Date de fin"
                    value={endDate}
                    onChange={handleEndDateChange}
                    ampm={false}
                    format="YYYY-MM-DD HH:mm:ss"
                    maxDate={dayjs()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            height: 40, // ✅ Réduit la hauteur
                            fontSize: "14px", // ✅ Texte plus petit
                            "& fieldset": { borderColor: "#ddd" }, // ✅ Bordure orange
                            "&:hover fieldset": { borderColor: "#e76f33" }, // ✅ Bordure plus foncée au survol
                            "&.Mui-focused fieldset": { borderColor: "#d95c1e" }, // ✅ Bordure plus intense en focus
                          },
                          "& .MuiInputLabel-root": {
                            color: "#ff7e41", // ✅ Couleur du label
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#d95c1e", // ✅ Couleur du label en focus
                          },
                        },
                      },
                    }} // ✅ Nouvelle méthode
                  />
                </LocalizationProvider>
              </div>

            </div>


            {/* Mémoire pleine */}
            {/* <div className="row pt-1">
                <label className="col-6">
                  <input
                    type="checkbox"
                    name="memoirePleine"
                    checked={filters.memoirePleine}
                    onChange={handleCheckboxChangeFilters}
                  />
                  <span>{translate('memoryFull')}</span>
                </label>
              </div> */}

            {/* Relay Active */}
            {/* <div className="row pt-1">
                <label className="col-6">
                  <input
                    type="checkbox"
                    name="relayActive"
                    checked={filters.relayActive}
                    onChange={handleCheckboxChangeFilters}
                  />
                  <span>{translate('relayActive')}</span>
                </label>
              </div> */}
          </div>
        </div>

          <label className="ml-auto">
            {translate("Show")}
            <select
              name="DataTables_Table_0_length"
              aria-controls="DataTables_Table_0"
              className="custom-select custom-select-sm form-control form-control-sm mr-2 ml-2"
              style={{ width: "66px" }}
              onChange={handleSelectChange}
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
              <option value="500">500</option>
            </select>
          </label>

          <button
            className="btn"
            style={{

              background: "none",
            }}
            type="button"
            data-bs-toggle="offcanvas"
            data-bs-target="#drawerMenu"
            aria-controls="drawerMenu"
          >
            <i className="las la-filter" style={{ fontSize: "24px" }}></i>
          </button>

          <Dropdown>
            <Dropdown.Toggle variant="" id="dropdown-basic" title={translate("Display columns")}>
              <i className="las la-eye"></i>
            </Dropdown.Toggle>

            <Dropdown.Menu>
              {columns.map((c) => (
                <Dropdown.Item
                  key={c.key}
                  as="button"
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={selectedColumns[c.key]}
                    onChange={() => handleColumnChange(c.key)}
                  />
                  <span style={{ marginLeft: "10px" }}>{c.label}</span>
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <Button
            variant="outline-primary"
            className="ml-2"
            onClick={() => navigate(`/preference?focusPage=${PAGE_ID}`)}
            title={translate("Preferences")}
          >
            <i className="las la-cog"></i>
          </Button>

        </div>
      </div>

      <div>
        <div className="row">
          <div className="table-responsive">
            <Table className="dataTable">
              <thead className="bg-white text-uppercase">
                <tr className="ligth ligth-data">
                  <th>
                    <input
                      type="checkbox"
                      checked={selectAllChecked}
                      onChange={(event) => handleSelectAllCheckboxChange(event.target.checked)}
                    />
                  </th>

                  {selectedColumns.immatriculation_vehicule && (
                    <th className="sorting" onClick={() => handleSortingColum("immatriculation_vehicule")}>
                      {translate("Vehicle")}
                    </th>
                  )}

                  {selectedColumns.psn_dispositif && (
                    <th className="sorting" onClick={() => handleSortingColum("psn_dispositif")}>
                      {translate("PSN")}
                    </th>
                  )}

                  {selectedColumns.nom_groupe && (
                    <th className="sorting" onClick={() => handleSortingColum("nom_groupe")}>
                      {translate("Vehicles Groups")}
                    </th>
                  )}

                  {selectedColumns.nom_user && (
                    <th className="sorting" onClick={() => handleSortingColum("nom_user")}>
                      {translate("User")}
                    </th>
                  )}

                  {selectedColumns.nom_conducteur && (
                    <th className="sorting" onClick={() => handleSortingColum("nom_conducteur")}>
                      {translate("Driver")}
                    </th>
                  )}

                  {selectedColumns.TIMESTAMP && (
                    <th className="sorting" onClick={() => handleSortingColum("TIMESTAMP")}>
                      {translate("Date Time")}
                    </th>
                  )}

                  {selectedColumns.GPSDIST && (
                    <th className="sorting" onClick={() => handleSortingColum("GPSDIST")}>
                      {translate("Distance")}
                    </th>
                  )}

                  {selectedColumns.GSMLVL && (
                    <th className="sorting" onClick={() => handleSortingColum("GSMLVL")}>
                      {translate("GSM")}
                    </th>
                  )}

                  {selectedColumns.SOG && (
                    <th className="sorting" onClick={() => handleSortingColum("SOG")}>
                      {translate("Speed")}
                    </th>
                  )}

                  <th>{translate("State")}</th>
                  <th>{translate("Actions")}</th>
                </tr>
              </thead>

              {loading ? (
                <tr>
                  <td colSpan={12}>
                    <div className="text-center">
                      <PropagateLoader color={"#123abc"} loading={loading} size={20} />
                    </div>
                  </td>
                </tr>
              ) : list_Fleet.length === 0 ? (
                <tr>
                  <td colSpan={12} className="text-center">
                    No vehicles available
                  </td>
                </tr>
              ) : (
                <>
                  {list_Fleet.map((item: any, index: number) => {
                    const rowId = `row_${index}`;
                    return (
                      <tbody key={item.id_vehicule} className="ligth-body">
                        <tr data-id={rowId}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedRows.has(index)}
                              onChange={(event) => handleCheckboxChange(index, event.target.checked)}
                            />
                          </td>

                          {selectedColumns.immatriculation_vehicule && (
                            <td>
                              <div className="d-flex align-items-center" onClick={() => handleRowClick(rowId, item.LAT, item.LON)}>
                                <span
                                  className="icon-chevron"
                                  style={{
                                    background: "#FFff",
                                    cursor: "pointer",
                                    marginRight: "10px",
                                    fontSize: "20px",
                                  }}
                                >
                                  <i className={`las ${expandedRow === rowId ? "la-chevron-down" : "la-chevron-right"}`}></i>
                                </span>

                                <div>
                                  <span
                                    id={`vehicle-${item.id_vehicule}`}
                                    style={{
                                      cursor: "pointer",
                                      position: "relative",
                                      color: copiedId === item.id_vehicule.toString() ? "#28a745" : "#007bff",
                                    }}
                                    title={translate("Click to copy the registration matriculation")}
                                    onClick={() => {
                                      if (item.immatriculation_vehicule && item.id_vehicule.toString()) {
                                        copyToClipboard(item.immatriculation_vehicule, item.id_vehicule.toString());
                                      }
                                    }}
                                  >
                                    <>
                                      <span style={{ color: copiedId === item.id_vehicule.toString() ? "#28a745" : "#007bff" }}>
                                        {item.immatriculation_vehicule}
                                      </span>

                                      {copiedId === item.id_vehicule.toString() && (
                                        <span
                                          style={{
                                            position: "absolute",
                                            top: "-20px",
                                            left: "50%",
                                            transform: "translateX(-50%)",
                                            backgroundColor: "#28a745",
                                            color: "#fff",
                                            padding: "2px 5px",
                                            borderRadius: "4px",
                                            fontSize: "12px",
                                          }}
                                        >
                                          {translate("Matriculation Copied")}
                                        </span>
                                      )}
                                    </>
                                  </span>

                                  <p className="mb-0">
                                    <small>{item.vehicule_type}</small>
                                  </p>
                                </div>
                              </div>
                            </td>
                          )}

                          {selectedColumns.psn_dispositif && <td>{item.psn_dispositif}</td>}
                          {selectedColumns.nom_groupe && <td>{item.nom_groupe}</td>}
                          {selectedColumns.nom_user && <td>{item.nom_user} {item.prenom_user} </td>}

                          {selectedColumns.nom_conducteur && (
                            <td>
                              {item.nom_conducteur && item.prenom_conducteur ? (
                                <>
                                  {item.IB_CODE && (
                                    <OverlayTrigger
                                      placement="top"
                                      delay={{ show: 250, hide: 1000 }}
                                      overlay={<Tooltip id="tooltip">{item.IB_CODE}</Tooltip>}
                                    >
                                      <span style={{ color: "#007bff", cursor: "pointer", userSelect: "text", display: "inline-block" }}>
                                        {" "} {item.nom_conducteur} {item.prenom_conducteur}
                                      </span>
                                    </OverlayTrigger>
                                  )}
                                </>
                              ) : item.IB_CODE ? (
                                <span title={item.IB_CODE} style={{ color: "#007bff", cursor: "pointer", userSelect: "text" }}>
                                  {item.IB_CODE}
                                </span>
                              ) : (
                                ""
                              )}
                            </td>
                          )}

                          {selectedColumns.TIMESTAMP && <td>{toTimestamp(item.TIMESTAMP)}</td>}
                          {selectedColumns.GPSDIST && <td>{Math.floor(item.GPSDIST / 1000)} km</td>}

                          {selectedColumns.GSMLVL && (
                            <td>
                              {item.GSMLVL !== null ? item.GSMLVL.toFixed(0) : "0"}%{" "}
                              {item.GSMLVL !== null && (
                                <i
                                  style={{ color: BarreReseau(item.GSMLVL).split("color:")[1].slice(0, -1) }}
                                  className={`las la-signal ${BarreReseau(item.GSMLVL)}`}
                                ></i>
                              )}
                            </td>
                          )}

                          {selectedColumns.SOG && (
                            <td style={{ minWidth: "85px" }}>
                              {item.SOG !== null && (
                                <i
                                  style={{ color: BarreSpeed(item.SOG).split("color:")[1].slice(0, -1) }}
                                  className={`${BarreSpeed(item.SOG)}`}
                                >
                                  <span>{item.SOG.toFixed(0)} Km/h</span>
                                </i>
                              )}
                            </td>
                          )}

                          <td>
                            <i className={ValidatePosition(item.NST)}></i>
                            <img
                              src={engineStat(item.ENGINESTAT, item.SOG, translate).iconState}
                              alt=""
                              title={engineStat(item.ENGINESTAT, item.SOG, translate).tooltipMessage}
                            />
                          </td>

                          <td>
                            <i
                              className="fas fa-map-marker-alt btn-zoom"
                              onClick={() => handleMapIconClick(item.LAT, item.LON, item)}
                              style={{
                                cursor: "pointer",
                                margin: "5px",
                                fontSize: "20px",
                                color: "red",
                                textDecoration: "underline",
                              }}
                            ></i>

                            <Link
                              to={`/position-tracking/${toTimestamp(item.TIMESTAMP)}/${toTimestamp(item.TIMESTAMP)}/${item.immatriculation_vehicule}`}
                              className="btn btn-light btn-zoom"
                              data-toggle="tooltip"
                              data-placement="top"
                              title={translate("Details")}
                            >
                              <i className="fas fa-route" style={{ fontSize: "1.2em" }}></i>
                            </Link>
                          </td>
                        </tr>

                        {expandedRow === rowId && (
                          <tr>
                            <td colSpan={12}>
                              <div className="responsive bordered hover">
                                <Table responsive>
                                  <thead>
                                    <tr>
                                      <td
                                        style={{ cursor: "pointer", textAlign: "center" }}
                                        className={`h6 ${selectedHeader === "additionalInfo" ? "table-active" : ""}`}
                                        onClick={() => setSelectedHeader("additionalInfo")}
                                      >
                                        {translate("Additional information")}
                                      </td>
                                      <td
                                        style={{ cursor: "pointer", textAlign: "center" }}
                                        className={`h6 ${selectedHeader === "gpsActivity" ? "table-active" : ""}`}
                                        onClick={() => setSelectedHeader("gpsActivity")}
                                      >
                                        {translate("Activate GPS")}
                                      </td>
                                    </tr>
                                  </thead>
                                </Table>

                                <Table className="overflow-auto">
                                  <tbody>
                                    {selectedHeader === "additionalInfo" && (
                                      <>
                                        <tr>
                                          <td colSpan={12}>{translate("Vehicle")}:</td>
                                          <td colSpan={12}>{item.immatriculation_vehicule}</td>
                                        </tr>

                                        <tr>
                                          <td colSpan={12}>{translate("Vehicles Groups")}:</td>
                                          <td colSpan={12}>{item.nom_groupe}</td>
                                        </tr>

                                        <tr>
                                          <td colSpan={12}>{translate("Date Time")}:</td>
                                          <td colSpan={12}>{toTimestamp(item.TIMESTAMP)}</td>
                                        </tr>

                                        <tr>
                                          <td colSpan={12}>{translate("Speed")}:</td>
                                          <td colSpan={12}>{item.SOG} km/h</td>
                                        </tr>

                                        <tr>
                                          <td colSpan={12}>{translate("Address")}:</td>
                                          <td colSpan={12}>
                                            {loadingAddress ? "Loading address..." : address}
                                            <i
                                              className="las la-map-marker-alt"
                                              onClick={() => handleMapIconClick(item.LAT, item.LON, item)}
                                              style={{ cursor: "pointer", marginLeft: "10px" }}
                                            ></i>
                                          </td>
                                        </tr>
                                      </>
                                    )}

                                    {selectedHeader === "gpsActivity" && (
                                      <tr>
                                        <td colSpan={12}>
                                          <LineChartComponent psnDispositif={item.psn_dispositif} />
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </Table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    );
                  })}
                </>
              )}
            </Table>

            <MapDrawer
              show={showMapModal}
              onClose={() => setShowMapModal(false)}
              lat={markerCoordinates.LAT}
              lng={markerCoordinates.LON}
              vehicle={selectedVehicle}
            />
          </div>

          <div className="row">
            <div className="col-md-6 d-flex align-items-center">
              <span>
                {translate("Displaying")} {list_Fleet.length} {translate("out of")} {total}
              </span>
            </div>

            <div className="col-md-6 d-flex justify-content-end">
              <ReactPaginate
                previousLabel={"previous"}
                nextLabel={"next"}
                breakLabel={"..."}
                pageCount={pageCount}
                marginPagesDisplayed={2}
                pageRangeDisplayed={3}
                onPageChange={handlePageClick}
                containerClassName={"pagination justify-content-center"}
                pageClassName={"page-item"}
                pageLinkClassName={"page-link"}
                previousClassName={"page-item"}
                previousLinkClassName={"page-link"}
                nextClassName={"page-item"}
                nextLinkClassName={"page-link"}
                breakClassName={"page-item"}
                breakLinkClassName={"page-link"}
                activeClassName={"active"}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
